import * as React from "react";

import { textContainerClassName } from "@1js/acui-common";

import {
  useAriaDescription,
  useAriaKeyShortcuts,
  useAriaLabel,
  useDataAutomationType,
  useDataNav,
  useDataUniqueId,
  useFunctionGetInputMethod,
  useShorthandFontIcon,
  useTabsterAttributes,
} from "../../../hooks";
import type { AppButtonProps, AppButtonState } from "./AppButton.types";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { useCommonButtonStyles } from "../../../styling/commonButtonStyles.styles";

export const useAppButtonState = (
  props: AppButtonProps,
  state: AppButtonState,
  setIsHovered?: React.Dispatch<React.SetStateAction<boolean>>
): AppButtonState => {
  const {
    dataAutomationType,
    dataNav,
    fullDescriptionLabel,
    id,
    keyShortcuts,
    label,
    location,
    onExecute,
    onMouseDown,
    onPointerEnter,
    onPointerLeave,
    shouldTakeFocus,
    takeFocusOnClick,
    hideLabel,
    toggled = false,
    ariaDescription,
    customTooltip,
    hoverIcon,
    iconColor,
    flipIconInRtl,
    onClick,
    isDoubleClickEnabled = false,
    hideIcon,
    disabled,
    tabsterAttributes,
  } = props;

  // Icon only considerations
  const iconOnly = !!hideLabel;

  // Always set iconOnly to false on the state, we want to render the children always even if it's "undefined"
  state.iconOnly = false;
  state.root.children = !iconOnly
    ? state.root.children || (
        <span className={textContainerClassName}>{label}</span>
      )
    : undefined;

  useAriaLabel(state, fullDescriptionLabel || label);
  useAriaKeyShortcuts(state, keyShortcuts);
  useDataAutomationType(state, dataAutomationType ?? "AppButton");
  useDataUniqueId(state, id, location);
  useDataNav(state, dataNav);
  useAriaDescription(state, ariaDescription || customTooltip);
  useTabsterAttributes(state, tabsterAttributes);

  const context = React.useContext(AppContext);
  const hideIconUsingToggleEnabled = !!checkExperiment(
    context,
    ribbonExperiments.fluentV9ToggleIconEnabled
  );
  const shouldHideIcon = hideIconUsingToggleEnabled && hideIcon;
  useShorthandFontIcon(state, iconColor, flipIconInRtl, shouldHideIcon);
  const getInputMethod = useFunctionGetInputMethod(state);

  const onHoverIconEnabled = !!checkExperiment(
    context,
    ribbonExperiments.onHoverIconEnabled
  );

  state.root.onClick = React.useCallback(
    (
      ev: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement, MouseEvent>
    ) => {
      if (disabled) {
        return;
      }
      if (onExecute) {
        onExecute({
          id,
          location,
          toggled,
          inputMethod: getInputMethod(ev),
          shouldTakeFocus,
          triggerTime: ev && ev.timeStamp,
          event: ev?.nativeEvent,
        });
        // In addition to what to the onExecute does, we also want to call the onClick
        // for cases like the split button where the onClick is used to close the dropdown menu
        onClick?.(ev);
      } else {
        onClick?.(ev);
      }
    },
    [
      onExecute,
      id,
      location,
      toggled,
      getInputMethod,
      shouldTakeFocus,
      onClick,
      disabled,
    ]
  );

  state.root.onMouseDown = React.useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onMouseDown) {
        onMouseDown(ev);
      }
      if (!takeFocusOnClick) {
        ev.preventDefault();
      }
    },
    [onMouseDown, takeFocusOnClick]
  );

  const handlePointerEnter = React.useCallback(
    (ev: React.PointerEvent<HTMLButtonElement & HTMLAnchorElement>) => {
      setIsHovered?.(true);
      onPointerEnter?.(ev);
    },
    [onPointerEnter, setIsHovered]
  );

  const handlePointerLeave = React.useCallback(
    (ev: React.PointerEvent<HTMLButtonElement & HTMLAnchorElement>) => {
      setIsHovered?.(false);
      onPointerLeave?.(ev);
    },
    [onPointerLeave, setIsHovered]
  );

  if (onHoverIconEnabled && hoverIcon) {
    state.root.onPointerEnter = handlePointerEnter;
    state.root.onPointerLeave = handlePointerLeave;
  }

  // Apply style to hide customTooltip when label is hidden
  useCommonButtonStyles(state, iconOnly);

  state.root.onDoubleClick = React.useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (isDoubleClickEnabled && onExecute) {
        onExecute({
          id,
          location,
          toggled,
          inputMethod: getInputMethod(ev),
          shouldTakeFocus,
          triggerTime: ev && ev.timeStamp,
          event: ev?.nativeEvent,
          isDoubleClick: true,
        });
      }
    },
    [
      getInputMethod,
      id,
      isDoubleClickEnabled,
      location,
      onExecute,
      shouldTakeFocus,
      toggled,
    ]
  );

  return state;
};
